import { useCallback, useEffect, useState } from "react";
import { usePaginatedSearch } from "../../hooks/use-paginated-search";
import { Spinner, Table } from "react-bootstrap";
import { api } from "../../helpers/apiCore";
import { endpoints } from "../../helpers/Endpoints";
import { USERS } from "../../lib/config";
import { formatCellValue } from "../../lib/format-cell";
import { Category, User } from "../../types";
import FormInput from "../../components/FormInput";
import PaginationComponent from "../../components/PaginationComponent";
import searchIcon from "../../assets/icons/search.svg";
import allUsersIcon from "../../assets/icons/all_users.svg";
import customerIcon from "../../assets/icons/lecturers.svg";
import studentIcon from "../../assets/icons/students.svg";
import otherUsersIcon from "../../assets/icons/other_users.svg";
import UserDetails from "./UserDetails";

// export const CATEGORIES: Category[] = [
//   { id: 'All', label: 'All Users', icon: allUsersIcon },
//   { id: 'customer', label: 'Customers', icon: customerIcon },
//   { id: 'student', label: 'Students', icon: studentIcon },
//   { id: 'regular', label: 'Other Users', icon: otherUsersIcon }
// ];

const UsersTable = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const {
    searchTerm,
    debouncedSearchTerm,
    paginationData,
    setPaginationData,
    handleSearchChange,
    handlePaginate
  } = usePaginatedSearch(10);

  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      api.setAuthorization(api.getLoggedInUser());
      const queryParams = new URLSearchParams({
        page: paginationData.currentPage.toString(),
        per_page: paginationData.perPage.toString(),
      });
      if (debouncedSearchTerm.trim()) queryParams.set('searchQuery', debouncedSearchTerm.trim());
      if (selectedCategory !== 'All') queryParams.set('user_type', selectedCategory.toUpperCase());

      const response = await api.get(endpoints.getAllUsers, queryParams);
      const { data, pageInfo } = response.data;

      setUsers(data || []);
      setPaginationData({
        currentPage: pageInfo?.currentPage || 1,
        totalPages: pageInfo?.totalPages || 1,
        total: pageInfo?.total || 0,
        perPage: pageInfo?.perPage || 10
      });
    } catch (error) {
      console.error("Error fetching users:", error);
      setUsers([]);
    } finally {
      setIsLoading(false);
    }
  }, [debouncedSearchTerm, paginationData.currentPage, paginationData.perPage, selectedCategory, setPaginationData]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleCategoryClick = (category: string) => {
    setSelectedCategory(category);
    setPaginationData(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleUserDetails = (user: User) => {
    setSelectedUserId(user.id)
		setShowModal(true);
	};

  const getSerialNumber = (index: number) =>
    (paginationData.currentPage - 1) * paginationData.perPage + index + 1;


  return (
    <>
      {/* <div className="categories">
        {CATEGORIES.map(({ id, label, icon }) => (
          <div
            key={id}
            className={`category ${selectedCategory === id ? 'category-active' : ''}`}
            onClick={() => handleCategoryClick(id)}
          >
            <img src={icon} alt={id} />
            <span>{label}</span>
          </div>
        ))}
      </div>        */}

      <div className="table-topbar">
        <div className="search_bar">
          <img src={searchIcon} alt="search" />
          <FormInput
            name="search"
            placeholder="Search users..."
            className="search-bar-input"
            value={searchTerm}
            onChange={e => handleSearchChange(e.target.value)}
          />
        </div>
      </div>

      <div className="table-body">
        <Table>
          <thead>
            <tr>
              {/* <th className="custom-thead-bg">S/N</th> */}
              {USERS.map(col => (
                <th key={col.key} className="custom-thead-bg">{col.label}</th>
              ))}
              <th className="custom-thead-bg">Actions</th>
            </tr>
          </thead>

          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={USERS.length + 2} className="text-center">
                  <Spinner animation="border" size="sm" />
                </td>
              </tr>
            ) : users?.length === 0 ? (
              <tr>
                <td colSpan={USERS.length + 2} className="text-center">
                  No users found.
                </td>
              </tr>
            ) : (
              users?.map((user, index) => (
                <tr key={user.id}>
                  {/* <td className="custom-tbody">{getSerialNumber(index)}</td> */}
                  {USERS.map(col => (
                    <td key={col.key} className="custom-tbody">{formatCellValue(col, user)}</td>
                  ))}
                   <td className="custom-tbody">
                    <p className="actions-btn" onClick={() => handleUserDetails(user)} >view details</p>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      {users?.length > 0 && (
        <PaginationComponent
          currentPage={paginationData.currentPage}
          totalPages={paginationData.totalPages}
          hasNextPage={paginationData.currentPage < paginationData.totalPages}
          hasPreviousPage={paginationData.currentPage > 1}
          nextPage={paginationData.currentPage + 1}
          previousPage={paginationData.currentPage - 1}
          onPaginate={handlePaginate}
        />
      )}

      {selectedUserId && (
        <UserDetails 
          onHide={() => {
            setShowModal(false);
            setSelectedUserId(null);
          }}
          userId={selectedUserId} 
        />
      )}
    </>
  );
};

export default UsersTable;
